// What a FOLDED rail heading says about the rows it hides (notifications-overhaul
// slice 01, D11). Pure: no DOM, no queries, no i18n.
//
//   dot    the worst row, `down` over `degraded`. `ok` never rolls up: `ok` is
//          the live accent, and the accent is never used on a heading
//          (`docs/standards/design-system.md`, "Accent means live").
//   count  the sum of OWED counts only. The caller says which rows are owed
//          (`OWED_COUNT_KEYS`, `lib/rail-counts.ts`); a row that is inventory
//          (repos) is not work, so it is not added.
//
// The honesty rule the rows follow holds here too. An unknown owed count is not
// a zero: it turns the sum into a floor (`atLeast`, painted as a trailing `+`).
// And a sum that is not above zero shows nothing at all, never `0` or `0+`.

import type { NavDotStatus } from '../components/AppNav';

/** One row as the rollup reads it: its route key and what its badges say NOW. */
export type RollupRow = {
  readonly key: string;
  readonly count: number | undefined;
  readonly dot: NavDotStatus | undefined;
};

export type RailRollup = {
  readonly dot: 'degraded' | 'down' | undefined;
  /** Above zero, or `undefined` for "show nothing". */
  readonly count: number | undefined;
  /** True when an owed count is unknown, so `count` is a floor. */
  readonly atLeast: boolean;
};

const NO_OWED: ReadonlySet<string> = new Set();

export function rollupRail(
  rows: readonly RollupRow[],
  owed: ReadonlySet<string> | undefined,
): RailRollup {
  // A rail with no owed rows at all (the admin host's) passes nothing.
  const owedKeys = owed ?? NO_OWED;
  let dot: RailRollup['dot'];
  let sum = 0;
  let unknown = false;
  for (const row of rows) {
    if (row.dot === 'down') dot = 'down';
    else if (row.dot === 'degraded' && dot === undefined) dot = 'degraded';
    if (!owedKeys.has(row.key)) continue;
    if (row.count === undefined) unknown = true;
    else sum += row.count;
  }
  return sum > 0
    ? { dot, count: sum, atLeast: unknown }
    : { dot, count: undefined, atLeast: false };
}
